import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { ImagePlus } from 'lucide-react';
import { useBlobUrl, useImageUrl } from '../db/useImageUrl';
import './CoverPicker.css';

/**
 * One photo well, used by the room dialog and the add-item dialog. It moved
 * here from `rooms/` in stage 2: the only thing that differed between the two
 * was the glyph behind an empty well, which is now a prop.
 *
 * `cover` is three-valued: a Blob is a newly picked file, `null` means the
 * user cleared it, and `undefined` means leave whatever the record already
 * has. The dialogs need that distinction to know whether to touch it at all.
 */
export function CoverPicker({
  label,
  existingImageId,
  cover,
  placeholder,
  onPick,
  onClear,
}: {
  label: string;
  existingImageId?: string | undefined;
  cover: Blob | null | undefined;
  /** Shown behind an empty well — a room's icon, or a generic image glyph. */
  placeholder: ReactNode;
  onPick: (file: Blob) => void;
  onClear: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);

  const pickedUrl = useBlobUrl(cover instanceof Blob ? cover : undefined);
  const storedUrl = useImageUrl(cover === undefined ? existingImageId : undefined);
  const url = pickedUrl ?? storedUrl;

  function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (file !== undefined) onPick(file);
    // Reset, or picking the same file twice in a row fires nothing.
    event.target.value = '';
  }

  return (
    <div className="field">
      <span className="section-label">{label}</span>

      <div className="cover-picker">
        <button
          type="button"
          className={`cover-picker__well${url === undefined ? ' cover-picker__well--empty' : ''}`}
          onClick={() => input.current?.click()}
          aria-label={url === undefined ? `Add a ${label.toLowerCase()}` : `Replace ${label.toLowerCase()}`}
        >
          {url === undefined ? (
            <span className="cover-picker__placeholder">
              {placeholder}
              <ImagePlus className="cover-picker__badge" size={18} aria-hidden />
            </span>
          ) : (
            <img className="cover-picker__image" src={url} alt="" />
          )}
        </button>

        {url !== undefined && (
          <button type="button" className="cover-picker__clear" onClick={onClear}>
            Remove {label}
          </button>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="image/*"
        className="cover-picker__input"
        onChange={onFile}
        tabIndex={-1}
      />
    </div>
  );
}
